import { useCallback, useMemo, useState } from "react";

import { useGetIdentity } from "@refinedev/core";

import { getStoredTokens } from "@/providers/auth/token-provider";
import type { IUser } from "@/types/types";
import { FhirResourceType, chunk, extractFhirResourceId, formatters } from "@/utils/fhir-utils";

import { useQuery, useQueryClient } from "@tanstack/react-query";

const COVERAGE_BASE_URL = `${import.meta.env.VITE_MEDLY_BASE_URL}/coverage`;

export interface CoverageAssignment {
  coveringFor: string[];
  coveredBy: string | null;
}

export type SetCoverageResult = { success: true } | { success: false; conflict: boolean };

export interface UseCoverageResult {
  coverageMap: Map<string, string>;
  isLoading: boolean;
  isSaving: boolean;
  setCoverage: (coveredPracId: string, coveringPracId: string) => Promise<SetCoverageResult>;
  removeCoverage: (coveredPracId: string, coveringPracId: string) => Promise<boolean>;
  refetch: () => Promise<unknown>;
}

interface UseCoverageArgs {
  practitionerIds?: string[];
  enabled?: boolean;
}

const BULK_CHUNK_SIZE = 100;
const COVERAGE_QUERY_KEY_ROOT = "coverage";
const COVERAGE_QUERY_KEY = [COVERAGE_QUERY_KEY_ROOT];

/**
 * Authenticated fetch helper for the coverage API.
 */
const coverageFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
  const tokens = getStoredTokens();
  if (!tokens?.accessToken) {
    throw new Error("Not authenticated. Please login again.");
  }

  return fetch(`${COVERAGE_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokens.accessToken}`,
      ...options.headers,
    },
  });
};

/**
 * Hook to manage coverage assignments via the dedicated coverage REST API.
 *
 * Fetches coverage via chunked POST /coverage/bulk requests, and provides
 * setCoverage/removeCoverage mutations.
 *
 * When called without args, resolves the logged-in practitioner's ID via useGetIdentity.
 * When called with explicit practitionerIds, uses those instead (for the practitioner management page).
 */
export const useCoverage = (args?: UseCoverageArgs): UseCoverageResult => {
  const { data: identity, isLoading: isIdentityLoading } = useGetIdentity<IUser>();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const identityPracId = identity?.fhirPractitionerId
    ? extractFhirResourceId(identity.fhirPractitionerId, FhirResourceType.Practitioner)
    : null;

  // When practitionerIds are provided, use them directly; otherwise fall back to the logged-in user's ID.
  const hasExplicitIds = args?.practitionerIds !== undefined;
  const explicitEnabled = args?.enabled ?? true;
  const argPractitionerIds = args?.practitionerIds;

  const practitionerIds = useMemo(
    () => (hasExplicitIds ? (argPractitionerIds ?? []).filter(Boolean) : identityPracId ? [identityPracId] : []),
    [hasExplicitIds, argPractitionerIds, identityPracId]
  );

  const isEnabled = hasExplicitIds ? explicitEnabled && practitionerIds.length > 0 : !!identityPracId;

  // Scope the queryKey by the requested practitioner IDs so concurrent callers with
  // different ID sets don't overwrite each other's cache entry. Prefix-matching
  // invalidations against ["coverage"] still clear every scoped entry.
  const scopedQueryKey = useMemo(
    () => [COVERAGE_QUERY_KEY_ROOT, [...new Set(practitionerIds)].sort().join(",")],
    [practitionerIds]
  );

  const {
    data: bulkCoverage,
    isLoading: isCoverageLoading,
    refetch,
  } = useQuery<Record<string, CoverageAssignment>>({
    queryKey: scopedQueryKey,
    enabled: isEnabled,
    queryFn: async () => {
      const refs = practitionerIds.map(id => formatters.formatPractitioner(id));
      const chunks = chunk(refs, BULK_CHUNK_SIZE);

      const results = await Promise.all(
        chunks.map(async chunkRefs => {
          try {
            const response = await coverageFetch("/bulk", {
              method: "POST",
              body: JSON.stringify({ practitionerIds: chunkRefs }),
            });

            if (!response.ok) {
              if (response.status === 404) {
                return {};
              }
              throw new Error(`Failed to fetch coverage: ${response.status}`);
            }

            const data: Record<string, CoverageAssignment> = await response.json();
            return data;
          } catch {
            return {};
          }
        })
      );

      // Merge all chunk results
      const merged: Record<string, CoverageAssignment> = {};
      for (const result of results) {
        Object.assign(merged, result);
      }
      return merged;
    },
  });

  // Derive coverageMap: coveredId → coveringId (bare IDs)
  const coverageMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!bulkCoverage) {
      return map;
    }

    for (const [key, assignment] of Object.entries(bulkCoverage)) {
      const thisId = extractFhirResourceId(key, FhirResourceType.Practitioner);
      if (!thisId) {
        continue;
      }

      // This practitioner is covered by someone
      if (assignment.coveredBy) {
        const coveringId = extractFhirResourceId(assignment.coveredBy, FhirResourceType.Practitioner);
        if (coveringId) {
          map.set(thisId, coveringId);
        }
      }

      // This practitioner is covering others
      for (const coveredRef of assignment.coveringFor) {
        const coveredId = extractFhirResourceId(coveredRef, FhirResourceType.Practitioner);
        if (coveredId) {
          map.set(coveredId, thisId);
        }
      }
    }
    return map;
  }, [bulkCoverage]);

  const setCoverage = useCallback(
    async (coveredPracId: string, coveringPracId: string): Promise<SetCoverageResult> => {
      setIsSaving(true);
      try {
        // Read fresh coverage data from query cache to avoid stale closure
        const currentCoverage = queryClient.getQueryData<Record<string, CoverageAssignment>>(scopedQueryKey);

        // Include all practitioners already covered by this coordinator to avoid replacing them
        const existingCovered = new Set<string>();
        if (currentCoverage) {
          const coveringRef = formatters.formatPractitioner(coveringPracId);
          for (const [key, assignment] of Object.entries(currentCoverage)) {
            // Check if this entry's coveredBy points to the coordinator
            if (assignment.coveredBy === coveringRef) {
              const bareId = extractFhirResourceId(key, FhirResourceType.Practitioner);
              if (bareId) {
                existingCovered.add(bareId);
              }
            }
            // Check if this is the coordinator's own entry with coveringFor
            const entryId = extractFhirResourceId(key, FhirResourceType.Practitioner);
            if (entryId === coveringPracId) {
              for (const ref of assignment.coveringFor) {
                const bareId = extractFhirResourceId(ref, FhirResourceType.Practitioner);
                if (bareId) {
                  existingCovered.add(bareId);
                }
              }
            }
          }
        }
        existingCovered.add(coveredPracId);

        const response = await coverageFetch(`/${coveringPracId}`, {
          method: "PUT",
          body: JSON.stringify({
            coveredPractitionerIds: Array.from(existingCovered).map(id => formatters.formatPractitioner(id)),
          }),
        });

        if (response.status === 409) {
          return { success: false, conflict: true };
        }

        if (!response.ok) {
          throw new Error(`Failed to set coverage: ${response.status}`);
        }

        queryClient.invalidateQueries({ queryKey: COVERAGE_QUERY_KEY });
        return { success: true };
      } catch {
        return { success: false, conflict: false };
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient, scopedQueryKey]
  );

  const removeCoverage = useCallback(
    async (coveredPracId: string, coveringPracId: string): Promise<boolean> => {
      setIsSaving(true);
      try {
        const response = await coverageFetch(`/${coveringPracId}/${coveredPracId}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          throw new Error(`Failed to remove coverage: ${response.status}`);
        }

        queryClient.invalidateQueries({ queryKey: COVERAGE_QUERY_KEY });
        return true;
      } catch {
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient]
  );

  // When the query transitions from disabled → enabled (identity just loaded),
  // React Query hasn't started fetching yet so isCoverageLoading is briefly false
  // even though no data exists. Guard against that gap by also checking for missing data.
  const isLoading = hasExplicitIds
    ? isCoverageLoading || (isEnabled && bulkCoverage === undefined)
    : isIdentityLoading || isCoverageLoading || (isEnabled && bulkCoverage === undefined);

  return {
    coverageMap,
    isLoading,
    isSaving,
    setCoverage,
    removeCoverage,
    refetch,
  };
};
